import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import cn from 'classnames';

import RiskLevelButton from '@components/RiskLevelButton/RiskLevelButton';
import SupplementResponseStatusDot from '@components/SupplementResponseStatusDot/SupplementResponseStatusDot';
import { TSupplementResponseStatus } from '@interfaces/supplements';
import useRiskLevelById from '@store/hooks/useRiskLevelById';

import { ISupplementResponseCardProps } from './SupplementResponseCard.interface';

import './style.scss';

const SupplementResponseCard: FC<ISupplementResponseCardProps> = ({
  className,
  id,
  name,
  status,
  riskLevelId,
  isActive,
  isDeactivated,
  onRemove,
  onShowDetails,
  setShowRiskLevelModal,
}) => {
  const { t } = useTranslation();
  const riskLevel = useRiskLevelById(riskLevelId);

  const handleShowDetails = useCallback(() => {
    onShowDetails(id);
  }, [onShowDetails, id]);

  const handleRemove = useCallback(() => {
    onRemove(id);
  }, [onRemove, id]);

  const handleShowRiskLevelModal = useCallback(() => {
    riskLevel && setShowRiskLevelModal(riskLevel.riskLevelId);
  }, [riskLevel, setShowRiskLevelModal]);

  return (
    <div
      className={cn(
        'supplement-response-card',
        { 'supplement-response-card_active': isActive },
        className,
      )}
    >
      {/* Separate clickable card surface to avoid event collisions with clickable risk level button below */}
      <div className="supplement-response-card__surface" onClick={handleShowDetails} />

      <div className="supplement-response-card__name">
        {name}

        {isDeactivated && (
          <div className="supplement-response-card__deactivated">
            {t('dietarySupplementInquiry.deactivated')}
          </div>
        )}
      </div>

      {status !== TSupplementResponseStatus.Complete && (
        <SupplementResponseStatusDot className="supplement-response-card__status" value={status} />
      )}

      {status === TSupplementResponseStatus.NeedsInfo && (
        <DeleteIcon
          className="supplement-response-card__delete-icon pointer"
          color="error"
          onClick={handleRemove}
        />
      )}

      {riskLevel && !isDeactivated && (
        <RiskLevelButton
          className="supplement-response-card__risk-level"
          riskLevel={riskLevel}
          onClick={handleShowRiskLevelModal}
          responsiveLabel
        />
      )}
    </div>
  );
};

export default SupplementResponseCard;
